// src/scenes/Organization/index.jsx
import React, { useEffect, useState } from "react";
import { authGet, authPost, authPatch, authDelete } from "../../lib/authApi";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/VpnKey";
import AddIcon from "@mui/icons-material/PersonAdd";

export default function OrganizationPage() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Dialog states
  const [showProfile, setShowProfile] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showReset, setShowReset] = useState({ open: false, user: null });

  // Form data
  const [profileData, setProfileData] = useState({});
  const [addUserData, setAddUserData] = useState({
    name: "",
    email: "",
    job_role: "",
    password: "",
  });
  const [resetPassword, setResetPassword] = useState("");

  // Load current user + full org on mount
  useEffect(() => {
    async function fetchUserAndOrg() {
      try {
        // 1) Who am I?
        const meRes = await authGet("/api/users/me");
        setMe(meRes);
        setProfileData(meRes);

        // 2) Am I an admin?
        const adminRoles = ["admin", "billing_admin", "Team Admin"];
        setIsAdmin(adminRoles.includes(meRes.job_role));

        // 3) Load everyone (always)
        const orgUsers = await authGet("/api/users/organization");
        setUsers(orgUsers);
      } catch (err) {
        console.error(err);
      }
    }
    fetchUserAndOrg();
  }, []);

  // Handlers

  function handleProfileClick() {
    setProfileData(me);
    setShowProfile(true);
  }

  async function handleProfileSave() {
    await authPatch("/api/users/me", profileData);
    setMe(profileData);
    setShowProfile(false);
  }

  async function handleAddUser() {
    await authPost("/api/users/organization", addUserData);
    setShowAdd(false);
    setAddUserData({ name: "", email: "", job_role: "", password: "" });
    setUsers(await authGet("/api/users/organization"));
  }

  function handleEditUser(u) {
    setProfileData(u);
    setShowProfile(true);
  }

  async function handleRemoveUser(id) {
    if (!window.confirm("Remove this user?")) return;
    await authDelete(`/api/users/organization/${id}`);
    setUsers(await authGet("/api/users/organization"));
  }

  async function handleResetPassword() {
    await authPatch(
      `/api/users/reset-password/${showReset.user.id}`,
      { password: resetPassword }
    );
    setShowReset({ open: false, user: null });
    setResetPassword("");
    alert("Password has been reset!");
  }

  return (
    <div>
      <h2>My Organization</h2>

      {/* — Your own profile — */}
      <div>
        <h3>You</h3>
        <p>
          <span
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={handleProfileClick}
          >
            {me?.name || "Unknown"} ({me?.email})
          </span>
        </p>
        <Button
          size="small"
          startIcon={<KeyIcon />}
          onClick={() => setShowReset({ open: true, user: me })}
        >
          Reset My Password
        </Button>
      </div>

      {/* — Full list for everyone — */}
      <div style={{ marginTop: 24 }}>
        <h3>All Users</h3>

        {/* only admins can invite */}
        {isAdmin && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            sx={{ mb: 1 }}
            onClick={() => setShowAdd(true)}
          >
            Invite User
          </Button>
        )}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.job_role}</TableCell>
                <TableCell align="right">
                  {/* you can edit yourself */}
                  {u.id === me?.id && (
                    <IconButton
                      onClick={handleProfileClick}
                      title="Edit your profile"
                    >
                      <EditIcon />
                    </IconButton>
                  )}

                  {/* admins can edit/reset/delete others */}
                  {isAdmin && u.id !== me?.id && (
                    <>
                      <IconButton
                        onClick={() => handleEditUser(u)}
                        title="Edit user"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => setShowReset({ open: true, user: u })}
                        title="Reset password"
                      >
                        <KeyIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveUser(u.id)}
                        title="Delete user"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* — Edit Profile Dialog — */}
      <Dialog open={showProfile} onClose={() => setShowProfile(false)}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={profileData.name || ""}
            onChange={(e) =>
              setProfileData({ ...profileData, name: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={profileData.email || ""}
            disabled
            fullWidth
            margin="normal"
          />
          <TextField
            label="Role"
            value={profileData.job_role || ""}
            disabled={!isAdmin}
            onChange={(e) =>
              setProfileData({ ...profileData, job_role: e.target.value })
            }
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProfile(false)}>Cancel</Button>
          <Button onClick={handleProfileSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* — Invite User Dialog — */}
      <Dialog open={showAdd} onClose={() => setShowAdd(false)}>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={addUserData.name}
            onChange={(e) =>
              setAddUserData({ ...addUserData, name: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={addUserData.email}
            onChange={(e) =>
              setAddUserData({ ...addUserData, email: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Role"
            value={addUserData.job_role}
            onChange={(e) =>
              setAddUserData({ ...addUserData, job_role: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Temporary Password"
            type="password"
            value={addUserData.password}
            onChange={(e) =>
              setAddUserData({ ...addUserData, password: e.target.value })
            }
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained">
            Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* — Reset Password Dialog — */}
      <Dialog
        open={showReset.open}
        onClose={() => setShowReset({ open: false, user: null })}
      >
        <DialogTitle>
          Reset Password for {showReset.user?.name || showReset.user?.email}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="New Password"
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReset({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={!resetPassword}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
